import React, { useState, useRef } from "react";
import { ToastContainer } from "react-toastify";
import useSwal from '../../common/ReusableComponent/useSwal';
import { useToastHandler } from '../../common/ToastHandler';
import JoditEditor from "../../common/JoditEditor";
import ReusableTable from "../../common/ReusableComponent/reusableTable/ReusableTable";
import { CustomButton } from "../../common/ReusableComponent/CustomButton";
import useApi from "../../../utils/api-manager/Helper/useApi";
import useMyPermissions from "../../../utils/permissions/useMyPermissions";
import Can from "../../../utils/permissions/Can";
const TEMPLATE_VARIABLES = [
  { label: "Full Name", value: "{{name}}" },
  { label: "First Name", value: "{{first_name}}" },
  { label: "Last Name", value: "{{last_name}}" },
  { label: "Email", value: "{{email}}" },
  { label: "Username", value: "{{username}}" },
  { label: "Contact Number", value: "{{contact_no}}" },
  { label: "Store Name", value: "{{store_name}}" },
  { label: "Role", value: "{{role}}" },
  { label: "Joining Date", value: "{{joining_date}}" },
  { label: "Document Title", value: "{{document_title}}" },
  { label: "Assignment Date", value: "{{assignment_date}}" },
];

const DEFAULT_SIGNER_ROLES = ["Signer", "Store Owner"];
const FIELD_TYPES = ["signature", "signature_date", "printed_name", "initials"];

const emptyVariable = () => ({
  key: "",
  label: "",
  data_type: "text",
  required: true,
  default_value: "",
  help_text: "",
});

const parseTemplateMeta = (rawContent) => {
  try {
    const parsed = typeof rawContent === "string" ? JSON.parse(rawContent) : rawContent;
    return parsed?.legal_meta || parsed || {};
  } catch {
    return {};
  }
};

const findPlaceholders = (html = "") => {
  const matches = String(html).match(/{{\s*[\w.]+\s*}}/g) || [];
  return [...new Set(matches.map((item) => item.replace(/[{}]/g, "").trim()))];
};

const Templates = () => {
  const { can } = useMyPermissions();
  const [templateContent, setTemplateContent] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [customVariables, setCustomVariables] = useState([]);
  const [draftVariable, setDraftVariable] = useState(emptyVariable());
  const [signerRoles, setSignerRoles] = useState(DEFAULT_SIGNER_ROLES);
  const [newRole, setNewRole] = useState("");
  const [selectedRole, setSelectedRole] = useState(DEFAULT_SIGNER_ROLES[0]);
  const [fieldType, setFieldType] = useState(FIELD_TYPES[0]);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const editorRef = useRef(null);
  const { GetPaginatedData, Post, Delete, Patch, Get, getAPI, PostWithId, getHost } = useApi();
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();
  const insertVariable = (variable) => {
    const editor = editorRef.current;
    if (!editor) return;
    //  editor.insertHTML(`<strong>${variable}</strong>&nbsp;`);
    editor.insertHTML(variable);
  };

  const resetTemplateMeta = () => {
    setCustomVariables([]);
    setDraftVariable(emptyVariable());
    setSignerRoles(DEFAULT_SIGNER_ROLES);
    setNewRole("");
    setSelectedRole(DEFAULT_SIGNER_ROLES[0]);
    setFieldType(FIELD_TYPES[0]);
  };

  const insertSignatureField = () => {
    const role = selectedRole || signerRoles[0] || "Signer";
    const token = `{{sig:${role}:${fieldType}}}`;
    insertVariable(token);
  };

  const addCustomVariable = () => {
    const key = draftVariable.key.trim().replace(/[{}]/g, "").replace(/\s+/g, "_").toLowerCase();
    if (!key) {
      handleToast({ type: "ERROR", message: "Variable key is required" });
      return;
    }
    if (customVariables.some((variable) => variable.key === key)) {
      handleToast({ type: "ERROR", message: "Variable already exists" });
      return;
    }
    setCustomVariables((prev) => [
      ...prev,
      {
        ...draftVariable,
        key,
        label: draftVariable.label || key.replace(/_/g, " "),
      },
    ]);
    setDraftVariable(emptyVariable());
  };

  const addSignerRole = () => {
    const role = newRole.trim();
    if (!role) return;
    if (signerRoles.some((item) => item.toLowerCase() === role.toLowerCase())) {
      handleToast({ type: "ERROR", message: "Signer role already exists" });
      return;
    }
    setSignerRoles((prev) => [...prev, role]);
    setSelectedRole(role);
    setNewRole("");
  };

  const handleCreateTemplate = () => {
    setTemplateContent("");
    setTemplateName("");
    setTemplateType("");
    setEditingId(null);
    resetTemplateMeta();
    setShowEditor(true);
  };
  //  const saveTemplate = async () => {
  //   const editor = editorRef.current;
  //   const latestContent = editor ? editor.getValue() : templateContent;
  //   if (!templateName || !templateType || !latestContent) {
  //     handleToast({
  //       type: "ERROR",
  //       message: "Please fill all fields"
  //     });
  //     return;
  //   }
  //   const payload = {
  //     name: templateName,
  //     template_type: templateType,
  //     content: latestContent
  //   };
  //      try {
  //       if (editingId) {
  //         const response = await Patch('template', editingId, payload);
  //         console.log('Patch response:', response);
  //         handleToast({
  //           type: "SUCCESS",
  //           message: 'Template updated successfully'
  //         });
  //       } else {
  //         const response = await Post('template', payload);
  //         handleToast({
  //           type: "SUCCESS",
  //           message: 'Template created successfully'
  //         });
  //       }
  //       setRefreshKey(prev => prev + 1);
  //       setShowEditor(false);
  //       setTemplateContent("");
  //       setTemplateName("");
  //       setTemplateType("");
  //       setEditingId(null);
  //     } catch (error) {
  //       console.error('Error saving template:', error);
  //       handleToast({
  //         type: "ERROR",
  //         message: 'Error saving template'
  //       });
  //     } finally {
  //       setLoading(false);
  //     }
  //   };


  const saveTemplate = async () => {
    const editor = editorRef.current;
    const latestContent = editor ? editor.getValue() : templateContent;

    const stripHtml = (html) => {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    };

    const plainText = stripHtml(latestContent).trim();

    if (!templateName || !templateType || !plainText) {
      handleToast({
        type: "ERROR",
        message: "Please fill all fields"
      });
      return;
    }

    const detectedVariables = findPlaceholders(latestContent)
      .filter((key) => !key.startsWith("sig:"))
      .map((key) => ({ key, label: key.replace(/_/g, " "), data_type: "text", required: false }));

    const variablesByKey = new Map(
      [...detectedVariables, ...customVariables].map((variable) => [variable.key, variable])
    );

    const payload = {
      name: templateName,
      template_type: templateType,
      content: latestContent,
      raw_content: JSON.stringify({
        legal_meta: {
          variables: [...variablesByKey.values()],
          signer_roles: signerRoles.filter(Boolean),
          signature_fields: findPlaceholders(latestContent)
            .filter((key) => key.startsWith("sig:"))
            .map((key) => {
              const [, role = "Signer", type = "signature"] = key.split(":");
              return { role, field_type: type, placeholder: `{{${key}}}`, required: true };
            }),
        },
      }),
    };

    try {
      setLoading(true);

      if (editingId) {
        await Patch("template", editingId, payload);

        handleToast({
          type: "SUCCESS",
          message: "Template updated successfully"
        });
      } else {
        await Post("template", payload);

        handleToast({
          type: "SUCCESS",
          message: "Template created successfully"
        });
      }

      setRefreshKey((prev) => prev + 1);
      setShowEditor(false);
      setTemplateContent("");
      setTemplateName("");
      setTemplateType("");
      resetTemplateMeta();
      setEditingId(null);
    } catch (error) {
      console.error("Error saving template:", error);

      handleToast({
        type: "ERROR",
        message: "Error saving template"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateData = async ({ page = 1, searchTerm = "" } = {}) => {
    try {
      const response = await GetPaginatedData('template', {
        page,
        search: searchTerm
      });
      return {
        data: response.data,
        totalPages: Math.ceil(response.totalCount / 10)
      };
    } catch (error) {
      console.error('Error fetching templates:', error);
      return {
        data: [],
        totalPages: 1
      };
    }
  };
  const handleOnDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      const response = await Delete('template', id);
      handleToast({
        type: "SUCCESS",
        message: response.message || 'Successfully deleted'
      });
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting template:', error);
      handleToast({
        type: "ERROR",
        message: 'Error deleting template'
      });
    }
  };
  const handleOnUpdate = async (id) => {
    try {
      setLoading(true);
      const templateUrl = `${getAPI('template')}${id}/`;
      const response = await Get(templateUrl);
      console.log('Template response:', response);
      const template = response || {};
      const meta = parseTemplateMeta(template.raw_content);
      setTemplateName(template.name || '');
      setTemplateType(template.template_type || '');
      setTemplateContent(template.content || '');
      setCustomVariables(meta.variables || []);
      setSignerRoles((meta.signer_roles?.length ? meta.signer_roles : DEFAULT_SIGNER_ROLES));
      setSelectedRole((meta.signer_roles?.[0] || DEFAULT_SIGNER_ROLES[0]));
      setEditingId(id);
      setShowEditor(true);
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleConvertToWord = async (id) => {
    try {
      setLoading(true);
      const response = await PostWithId('template', `${id}/convert-word/`, {});
      console.log('Convert response:', response);
      const fileUrl = response.word_file_url || response.data?.word_file_url;
      const message = response.message || response.data?.message || 'Word document generated successfully';
      if (fileUrl) {
        const fullUrl = `${getHost()}${fileUrl}`;
        window.open(fullUrl, '_blank');
        handleToast({
          type: "SUCCESS",
          message: message
        });
      } else {
        console.error('Response structure:', response);
        throw new Error('No file URL in response');
      }
    } catch (error) {
      console.error('Error converting to Word:', error);
      handleToast({
        type: "ERROR",
        message: 'Error generating Word document'
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDuplicateTemplate = async (id) => {
    try {
      setLoading(true);
      const template = await Get(`${getAPI('template')}${id}/`);
      await Post("template", {
        name: `${template.name || "Template"} Copy`,
        template_type: template.template_type,
        content: template.content,
        raw_content: template.raw_content,
      });
      handleToast({
        type: "SUCCESS",
        message: "Template duplicated successfully"
      });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error duplicating template:", error);
      handleToast({
        type: "ERROR",
        message: "Error duplicating template"
      });
    } finally {
      setLoading(false);
    }
  };
  const columns = [
    { header: "Template Name", field: "name" },
    { header: "Category", field: "template_type" },
    {
      header: "Status",
      render: () => (
        <span style={{ padding: "5px 10px", borderRadius: "999px", background: "#ecfdf5", color: "#047857", fontSize: "12px", fontWeight: 700 }}>
          Active
        </span>
      ),
    },
    {
      header: "Variables",
      render: (row) => parseTemplateMeta(row.raw_content).variables?.length || findPlaceholders(row.content).length || 0,
    },
    {
      header: "Signer Roles",
      render: (row) => parseTemplateMeta(row.raw_content).signer_roles?.join(", ") || "Signer",
    },
  ];
  if (showEditor) {
    return (
      <div className="responsive-box">
        <div className="header-row">
          <div className="setting-title">{editingId ? 'Edit Template' : 'Template Editor'}</div>
          <CustomButton
            text="Back to Templates"
            onClick={() => {
              setShowEditor(false);
              setEditingId(null);
              resetTemplateMeta();
            }}
            color="#000000"
            backgroundColor="#6c757d"
          />
        </div>
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
            <input
              type="text"
              placeholder="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc", flex: 1 }}
            />
            <input
              type="text"
              placeholder="Template Type"
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
              style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc", flex: 1 }}
            />
            <CustomButton
              text={editingId ? "Update Template" : "Save Template"}
              onClick={saveTemplate}
              color="#ffffff"
              backgroundColor="#28a745"
              disabled={loading}
            />
          </div>
          <div style={{ display: "flex", flexDirection: window.innerWidth < 768 ? "column" : "row", gap: "15px" }}>
            <div style={{ flex: window.innerWidth < 768 ? "1" : "3" }}>
              <JoditEditor
                ref={editorRef}
                value={templateContent}
                onblur={(content) => setTemplateContent(content)}
                placeholder="Create your legal template here..."
                height={window.innerWidth < 768 ? "300px" : "500px"}
              />
            </div>
            <div style={{
              flex: window.innerWidth < 768 ? "1" : "1",
              border: "1px solid #ddd",
              borderRadius: "6px",
              padding: "10px",
              background: "#f9f9f9",
              maxHeight: window.innerWidth < 768 ? "300px" : "500px",
              overflowY: "auto"
            }}>
              <h4 style={{ margin: "0 0 15px 0", fontSize: "16px" }}>Template Variables</h4>
              {TEMPLATE_VARIABLES.map((item) => (
                <button
                  key={item.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertVariable(item.value);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    marginBottom: "8px",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                >
                  <div style={{ fontWeight: "bold", marginBottom: "2px" }}>{item.label}</div>
                  <div style={{ color: "#666", fontSize: "13px" }}>{item.value}</div>
                </button>
              ))}

              <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "14px", paddingTop: "14px" }}>
                <h4 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>Custom Variables</h4>
                <input
                  type="text"
                  placeholder="Variable key, e.g. employee_name"
                  value={draftVariable.key}
                  onChange={(e) => setDraftVariable((prev) => ({ ...prev, key: e.target.value }))}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "8px" }}
                />
                <input
                  type="text"
                  placeholder="Display label"
                  value={draftVariable.label}
                  onChange={(e) => setDraftVariable((prev) => ({ ...prev, label: e.target.value }))}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "8px" }}
                />
                <select
                  value={draftVariable.data_type}
                  onChange={(e) => setDraftVariable((prev) => ({ ...prev, data_type: e.target.value }))}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "8px" }}
                >
                  {["text", "multiline", "number", "date", "email", "phone", "address", "currency", "checkbox"].map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <label style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px", fontSize: "13px" }}>
                  <input
                    type="checkbox"
                    checked={draftVariable.required}
                    onChange={(e) => setDraftVariable((prev) => ({ ...prev, required: e.target.checked }))}
                  />
                  Required variable
                </label>
                <button
                  type="button"
                  onClick={addCustomVariable}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "0", background: "#1f2937", color: "#fff", cursor: "pointer", marginBottom: "10px" }}
                >
                  Add Variable
                </button>
                {customVariables.map((variable) => (
                  <button
                    key={variable.key}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertVariable(`{{${variable.key}}}`);
                    }}
                    style={{ width: "100%", textAlign: "left", marginBottom: "6px", padding: "8px", borderRadius: "6px", border: "1px solid #bfdbfe", background: "#eff6ff", cursor: "pointer" }}
                  >
                    <strong>{variable.label}</strong>
                    <div style={{ color: "#2563eb", fontSize: "12px" }}>{`{{${variable.key}}}`} • {variable.data_type}</div>
                  </button>
                ))}
              </div>

              <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "14px", paddingTop: "14px" }}>
                <h4 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>Signer Roles & Fields</h4>
                <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                  <input
                    type="text"
                    placeholder="Add role"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    style={{ minWidth: 0, flex: 1, padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                  />
                  <button type="button" onClick={addSignerRole} style={{ border: 0, borderRadius: "6px", padding: "0 10px", background: "#22c55e", color: "#fff" }}>+</button>
                </div>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "8px" }}
                >
                  {signerRoles.map((role) => <option key={role} value={role}>{role}</option>)}
                </select>
                <select
                  value={fieldType}
                  onChange={(e) => setFieldType(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "8px" }}
                >
                  {FIELD_TYPES.map((type) => <option key={type} value={type}>{type.replace("_", " ")}</option>)}
                </select>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertSignatureField();
                  }}
                  style={{ width: "100%", padding: "9px", borderRadius: "6px", border: "1px solid #f59e0b", background: "#fffbeb", color: "#92400e", cursor: "pointer", fontWeight: 700 }}
                >
                  Insert Signing Field
                </button>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
                  {signerRoles.map((role) => (
                    <span key={role} style={{ padding: "5px 8px", borderRadius: "999px", background: "#ecfdf5", color: "#047857", fontSize: "12px", fontWeight: 700 }}>
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <ToastContainer />
      <div className="responsive-box">
        <div className="header-row">
          <div className="setting-title">Legal Templates</div>
          <Can module="legalManagementService" action="add">
            <CustomButton
              text="Create Template"
              onClick={handleCreateTemplate}
              color="#000000"
              backgroundColor="#FFAF32"
            />
          </Can>
        </div>
        <ReusableTable
          key={refreshKey}
          fetchData={fetchTemplateData}
          columns={columns}
          showAction={true}
          showFooter={true}
          onDelete={can("legalManagementService", "delete") ? handleOnDelete : undefined}
          onEdit={can("legalManagementService", "change") ? handleOnUpdate : undefined}
          loading={loading}
          showDelete={true}
          customActions={[
            {
              label: "Duplicate",
              onClick: handleDuplicateTemplate,
              color: "#1f2937",
              backgroundColor: "#fff",
              border: "1px solid #cbd5e1"
            },
            {
              label: "Convert to Word",
              onClick: handleConvertToWord,
              color: "#dc3545",
              backgroundColor: "#fff",
              border: "1px solid #dc3545"
            }
          ]}
        />
      </div>
    </>
  );
};
export default Templates;
